// /help/articles/:articleSlug — one article (HTML from helpContent.json).
import React from 'react';
import { Helmet } from 'react-helmet';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import AppTransition from '../components/appComponents/AppTransition';
import { articleBySlug, collectionBySlug, getRelatedArticles, helpArticles } from './helpUtils';
import { ArticleList, ContactCard } from './helpParts';
import './helpCenter.css';

const formatDate = value => (value
  ? new Intl.DateTimeFormat('en', { month: 'long', day: 'numeric', year: 'numeric' }).format(new Date(value))
  : null);

const HelpArticle = () => {
  const { articleSlug } = useParams();
  const navigate = useNavigate();
  const article = articleBySlug[articleSlug];

  if (!article) {
    // A renamed article keeps its number, so an old link still finds it
    const renamed = helpArticles.find(a => a.slug.split('-')[0] === articleSlug.split('-')[0]);
    return <Navigate to={renamed ? `/help/articles/${renamed.slug}` : '/help'} replace />;
  }

  const collection = collectionBySlug[article.collectionSlug];
  const related = getRelatedArticles(article);
  const updated = formatDate(article.updatedAt);

  // Links inside the article HTML: other app pages stay in the app, screenshots open in a new tab
  const onBodyClick = e => {
    const a = e.target.closest('a');
    const href = a?.getAttribute('href');
    if (!href || !href.startsWith('/') || e.metaKey || e.ctrlKey || e.shiftKey) return;
    e.preventDefault();
    if (href.startsWith('/help-assets/')) window.open(href, '_blank', 'noopener');
    else navigate(href);
  };

  return (
    <AppTransition key={article.slug}>
      <div className="help-page help-page--article">
        <Helmet><title>{`Bumpups | ${article.title}`}</title></Helmet>
        <nav className="help-crumbs" aria-label="Breadcrumb">
          <Link to="/help">Help</Link>
          {collection && (
            <>
              <span aria-hidden="true">/</span>
              <Link to={`/help/${collection.slug}`}>{collection.title}</Link>
            </>
          )}
        </nav>

        <header className="help-article-head">
          <h1 className="help-article-title">{article.title}</h1>
          {article.description && <p className="help-subtitle">{article.description}</p>}
          {updated && <p className="help-article-date">Updated {updated}</p>}
        </header>

        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div className="help-article-body" onClick={onBodyClick} dangerouslySetInnerHTML={{ __html: article.bodyHtml }} />

        {related.length > 0 && (
          <section className="help-section">
            <h2 className="help-section-title">Related articles</h2>
            <ArticleList articles={related} />
          </section>
        )}

        <ContactCard />
      </div>
    </AppTransition>
  );
};

export default HelpArticle;
